import { Component, Input, EventEmitter, Output } from '@angular/core';
import { CustomDateValidator } from 'app/commons/custom-date-validator';

export interface Day {
    day: number;
    month: number;
    year: number;
    date: Date;
}

@Component({
    selector: 'app-date',
    templateUrl: './date.component.html',
    styleUrls: ['./date.component.scss']
})

export class DateComponent {

    date: Date;
    month: number;
    year: number;

    min: number;
    max: number;

    currentMonth: number;
    currentDay: number;
    currentYear: number;

    showingMonth: Boolean;
    showingYear: Boolean;

    calendarDays: Day[];

    months: string[] = ['Enero', 'Febrero', 'Marzo', 'Abril',
        'Mayo', 'Junio', 'Julio', 'Agosto',
        'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

    years: number[] = [];

    @Input() minYear;

    // Fecha tope (fin del día) ya convertida a Date. Null = sin tope, que es el
    // comportamiento de siempre para las pantallas que no informan "maxDate".
    maxDateValue: Date = null;

    /**
     * Fecha máxima seleccionable en formato 'dd-MM-yyyy'. Deshabilita los días y
     * meses posteriores y recorta la lista de años.
     */
    @Input() set maxDate(value: string) {
        this.maxDateValue = this.parseMaxDate(value);
        this.chargedYears();
        if (undefined !== this.month) {
            this.showCalendar();
        }
    }

    @Input() set value(value: String) {
        let converted: Date = null;
        if ((undefined === value) || (null === value)) {
            converted = new Date();
        } else {
            const pattern = /(\d{2})\-(\d{2})\-(\d{4})/;
            converted = new Date(value.replace(pattern, '$3-$2-$1'));
            this.chargedYears();
        }
        this.date = converted;
        this.month = converted.getMonth();
        this.year = converted.getFullYear();
        this.showCalendar();
    }

    @Output() selected = new EventEmitter<Date>();

    constructor() {
        this.chargedYears();
    }

    chargedYears() {
        let converted: Date = null;
        const temp: Date = new Date();
        this.currentMonth = temp.getMonth();
        this.currentDay = temp.getDate();
        if (!this.minYear) {
            this.currentYear = temp.getFullYear();
            this.years = [];
        } else {
            const pattern = /(\d{2})\-(\d{2})\-(\d{4})/;
            converted = new Date(this.minYear.replace(pattern, '$3-$2-$1'));
            this.currentYear = converted.getFullYear();
            this.years = [];
        }

        let startYear = this.currentYear - 10;
        this.min = startYear;
        while (startYear <= (this.currentYear + 10)) {
            this.years.push(startYear);
            startYear = startYear + 1;
        }
        this.max = startYear - 1;

        // El tope se aplica aquí y no solo al pintar los días porque "this.max" es
        // lo que frena la navegación por meses, y el selector de año ofrece +10
        // años por defecto: sin recortarlo se podría llegar al futuro por ahí.
        if (null !== this.maxDateValue) {
            const maxYear = this.maxDateValue.getFullYear();
            if (this.max > maxYear) {
                this.max = maxYear;
            }
            this.years = this.years.filter(year => year <= maxYear);
        }
    }

    /**
     * Convierte la fecha tope a Date situada al FINAL del día: la propia fecha
     * máxima tiene que seguir siendo seleccionable.
     */
    private parseMaxDate(value: string): Date {
        if ((undefined === value) || (null === value) || ('' === value)) {
            return null;
        }
        const pattern = /(\d{2})\-(\d{2})\-(\d{4})/;
        const converted = new Date(value.replace(pattern, '$3-$2-$1'));
        if (isNaN(converted.getTime())) {
            return null;
        }
        converted.setHours(23, 59, 59, 999);
        return converted;
    }

    isDisabled(day: Day): boolean {
        return (null !== this.maxDateValue) && (day.date.getTime() > this.maxDateValue.getTime());
    }

    isMonthDisabled(month: number): boolean {
        return this.isMonthAfterMax(this.year, month);
    }

    private isMonthAfterMax(year: number, month: number): boolean {
        if (null === this.maxDateValue) {
            return false;
        }
        return new Date(year, month, 1).getTime() > this.maxDateValue.getTime();
    }

    onShowMonth() {
        this.showingYear = false;
        this.showingMonth = !this.showingMonth;
    }

    onShowYear() {
        this.showingMonth = false;
        this.showingYear = !this.showingYear;
    }

    onPreviousMonth() {
        this.date.setMonth(this.date.getMonth() - 1);

        if (this.date.getFullYear() < this.min) {
            this.date.setMonth(this.date.getMonth() + 1);
            return;
        }

        this.month = this.date.getMonth();
        this.year = this.date.getFullYear();
        this.selected.emit(this.date);
        this.showCalendar();
    }

    onNextMonth() {
        this.date.setMonth(this.date.getMonth() + 1);

        // El control por año no basta cuando el tope cae a mitad de año: hay que
        // comparar también el mes al que se salta contra la fecha máxima.
        if ((this.date.getFullYear() > this.max)
            || this.isMonthAfterMax(this.date.getFullYear(), this.date.getMonth())) {
            this.date.setMonth(this.date.getMonth() - 1);
            return;
        }

        this.month = this.date.getMonth();
        this.year = this.date.getFullYear();
        this.selected.emit(this.date);
        this.showCalendar();
    }

    monthName(): String {
        return this.months[this.month];
    }

    onSetMonth(value: number) {
        if (this.isMonthDisabled(value)) {
            return;
        }
        this.date.setMonth(value);
        this.month = this.date.getMonth();
        this.year = this.date.getFullYear();
        this.selected.emit(this.date);
        this.showingMonth = false;
        this.showCalendar();
    }

    onSetYear(value: number) {
        this.date.setFullYear(value);
        this.year = this.date.getFullYear();
        this.selected.emit(this.date);
        this.showingYear = false;
        this.showCalendar();
    }

    onSetNow() {
        // El botón "Hoy" es otra vía para fijar fecha: si el tope fuese anterior a
        // hoy también tiene que respetarlo.
        if ((null !== this.maxDateValue) && (Date.now() > this.maxDateValue.getTime())) {
            return;
        }
        this.date = new Date();
        this.year = this.date.getFullYear();
        this.month = this.date.getMonth();
        this.selected.emit(this.date);
        this.showingMonth = false;
        this.showingYear = false;
        this.showCalendar();
    }

    onSetDate(day: Day) {
        // Los días por encima del tope se pintan deshabilitados; aquí se corta
        // también el click para que el estilo no sea la única barrera.
        if (this.isDisabled(day)) {
            return;
        }
        this.date = day.date;
        this.year = this.date.getFullYear();
        this.month = this.date.getMonth();
        this.selected.emit(this.date);
        this.showingMonth = false;
        this.showingYear = false;
        this.showCalendar();
    }

    showCalendar() {

        const temp: Date = new Date();
        const newCalendar: Day[] = [];
        let i;

        temp.setDate(1);
        temp.setMonth(this.month);
        temp.setFullYear(this.year);

        const pn = temp.getDay() === 0 ? 7 : temp.getDay();
        for (i = 1; i < pn; i++) {
            const prevDay = new Date(temp);
            prevDay.setDate(prevDay.getDate() - i);
            const dayToAdd = {
                day: prevDay.getDate(),
                month: prevDay.getMonth(),
                year: prevDay.getFullYear(),
                date: prevDay
            };
            newCalendar.push(dayToAdd);
        }

        newCalendar.reverse();

        const monthDay = new Date(temp);
        while (monthDay.getMonth() === temp.getMonth()) {
            const dayToAdd = {
                day: monthDay.getDate(),
                month: monthDay.getMonth(),
                year: monthDay.getFullYear(),
                date: new Date(monthDay)
            };
            newCalendar.push(dayToAdd);
            monthDay.setDate(monthDay.getDate() + 1);
        }

        temp.setMonth(temp.getMonth() + 1);
        temp.setDate(temp.getDate() - 1);
        const nn = 7 - temp.getDay();

        for (i = 0; i < nn; i++) {
            const nextDay = new Date(temp);
            nextDay.setDate(nextDay.getDate() + i + 1);
            const dayToAdd = {
                day: nextDay.getDate(),
                month: nextDay.getMonth(),
                year: nextDay.getFullYear(),
                date: nextDay
            };
            newCalendar.push(dayToAdd);
        }

        this.calendarDays = newCalendar;

    }
}